import { useEffect, useRef } from "react";
import "@/App.css";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  Linkedin,
  ArrowUpRight,
  Download,
  Sparkles,
  Globe,
} from "lucide-react";
import Teaser from "./Teaser";
import NotFound from "./NotFound";

// Toggle: when true, root "/" shows the teaser and full site lives at "/site".
// Flip to false on launch day to make the full site the homepage.
const TEASER_MODE = false;

/* ============================================================
   DATA
   ============================================================ */
const PROFILE = {
  name: "Rishikesh N",
  title: "AI Explorer — Brands, Corporate & Film Content",
  tagline:
    "Strategic content & communications leader. 20+ years shaping narratives across journalism, broadcasting, advertising, and AI-driven storytelling.",
  email: "rishikeshn@me.com",
  phones: [
    { region: "Malaysia", number: "+60 12 914 8694", tel: "+60129148694" },
    { region: "India", number: "+91 96113 31976", tel: "+919611331976" },
  ],
  locations: [
    { city: "Kuala Lumpur", country: "Malaysia" },
    { city: "Bengaluru", country: "India" },
  ],
  location: "Kuala Lumpur · Bengaluru",
  linkedin: "https://www.linkedin.com/in/shambomahadeva/",
  resume: "/assets/rishikesh-resume.pdf",
  hero: "/assets/rishikesh-hero.png",
};

const EXPERTISE = [
  "Strategic Communications",
  "Brand Narrative",
  "Content Architecture",
  "Digital Transformation",
  "Creative & Film Direction",
  "Visual Storytelling",
  "AI-Enhanced Content",
  "Audience Engagement",
  "Cross-Functional Leadership",
  "Integrated Marketing",
  "Campaign Ideation",
  "Platform Innovation",
];

const EXPERIENCE = [
  {
    company: "Vaikuntha Media",
    role: "AI Explorer — Brands, Corporate & Film Content",
    period: "May 2025 — Present",
    location: "Bengaluru, India",
    body:
      "Exploring AI-driven storytelling and content innovation across brand, corporate, and film communication.",
  },
  {
    company: "Vision Reality Tech Studio",
    role: "Director — Digital, Content, Creative & Comms",
    period: "Jul 2014 — May 2025",
    location: "Kuala Lumpur · Bengaluru",
    body:
      "Built and led the agency's digital and content division across Malaysia and India. Delivered platform-based content ecosystems for healthcare, education, and Islamic finance. Integrated generative AI into ideation and creative workflows.",
  },
  {
    company: "Eight Seconds Sdn Bhd",
    role: "Consultant — Client Servicing & Digital Strategy",
    period: "Jul 2011 — Jul 2014",
    location: "Kuala Lumpur, Malaysia",
    body:
      "Shaped digital communication services and aligned solutions with evolving client needs. Bridged strategy and execution through cross-functional collaboration.",
  },
  {
    company: "DCR Vision Media (Citymedia)",
    role: "Content & Creative Director — Innovative Media",
    period: "Jul 2009 — Jul 2011",
    location: "India",
    body:
      "Built and launched a large-scale Out-of-Home digital media platform across 700+ locations. Led concept-to-execution, content strategy, branding, and tech integration.",
  },
  {
    company: "BRAND Communications",
    role: "Associate Creative Director",
    period: "Earlier",
    location: "Kuala Lumpur, Malaysia",
    body:
      "Led integrated communication strategies across digital and conventional platforms. Helped clients transition into digital-first storytelling.",
  },
  {
    company: "Sony Entertainment Television (Sony PIX)",
    role: "On-Air Promo Producer",
    period: "Earlier",
    location: "Mumbai, India",
    body:
      "Supported the launch and branding of Sony PIX. Directed promo campaigns, channel packaging, and multi-platform content strategies.",
  },
  {
    company: "Zee Telefilms Ltd.",
    role: "Promo Producer / Director",
    period: "Earlier",
    location: "Mumbai, India",
    body:
      "Led promo and packaging across multiple channels, including rebranding projects. End-to-end creative direction, production, and content strategy.",
  },
  {
    company: "WEBPercept (Percept India)",
    role: "Team Lead — Content",
    period: "Earlier",
    location: "Mumbai, India",
    body:
      "Progressed from copywriter to team lead in one of India's earliest digital divisions. Built content strategies for 20+ clients during the dot-com era.",
  },
  {
    company: "The Indian Express",
    role: "Journalist Trainee",
    period: "Earliest",
    location: "Mumbai, India",
    body:
      "Contributed research-driven editorial content for business supplements. Built foundational expertise in storytelling and structured communication.",
  },
];

const HIGHLIGHTS = [
  {
    title: "Nationwide OOH Digital Media Platform",
    span: "700+ locations · concept to rollout",
    body:
      "Built a large-scale Out-of-Home digital media platform end-to-end. Scaled creative, operations, partnerships and compliance.",
    accent: "01",
    size: "lg",
  },
  {
    title: "Sony PIX Launch & Branding",
    span: "Channel launch · multi-platform",
    body:
      "Directed promo campaigns, channel packaging and content strategies across broadcast and emerging digital media.",
    accent: "02",
    size: "md",
  },
  {
    title: "Digital-First Higher-Ed Ecosystems",
    span: "Webinars · virtual engagement",
    body:
      "Designed and launched digital-first communication ecosystems for higher-education institutions.",
    accent: "03",
    size: "md",
  },
  {
    title: "AI-Driven Storytelling",
    span: "Brand · Corporate · Film",
    body:
      "Exploring AI-assisted narrative concepts, generative ideation and scalable content approaches with creators and teams.",
    accent: "04",
    size: "lg",
  },
];

const LANGUAGES = [
  { name: "English", level: "Advanced · C1" },
  { name: "Tamil", level: "Advanced · C1" },
  { name: "Malayalam", level: "Advanced · C1" },
  { name: "Malay", level: "Advanced · C1" },
  { name: "Hindi", level: "Upper-Intermediate · B2" },
];

const SERVICES = [
  {
    n: "01",
    t: "Brand Positioning",
    body: "Define what you stand for — and why it should matter. Clarity before communication.",
  },
  {
    n: "02",
    t: "Narrative Systems",
    body: "Build messaging that stays consistent across teams, platforms, and time. Not campaigns — systems.",
  },
  {
    n: "03",
    t: "Content as a Business Asset",
    body: "Design and develop content assets that do more than publish — they educate, influence, and drive decisions.",
  },
  {
    n: "04",
    t: "Digital Brand Ecosystems",
    body: "Build structured digital properties (web, platforms, content hubs) that act as owned brand environments — not just online presence.",
  },
  {
    n: "05",
    t: "Strategic Advisory",
    body: "Work directly with founders and teams on critical communication decisions. When messaging impacts business — not just marketing.",
  },
];

const CASE_STUDIES = [
  {
    id: "ijn",
    sector: "Healthcare · Malaysia",
    brand: "IJN",
    brandFull: "Institut Jantung Negara — National Heart Institute",
    headline: "From content production to patient-driven conversations.",
    context:
      "Worked with IJN (National Heart Institute, Malaysia) — one of Asia's leading cardiac hospitals — to rethink their YouTube content strategy.",
    challenge: {
      summary: "The existing approach followed a typical model:",
      points: ["Produce content", "Run ads", "Chase views"],
      conclusion: "High visibility, but low meaningful engagement.",
    },
    approach: {
      summary: "Shifted from content output → audience understanding.",
      points: [
        "Conducted on-ground research with patients (with permission)",
        "Identified real concerns, questions, and anxieties",
        "Built a structured content format: IJN Heart Talk",
        "Designed topics and questions around actual patient intent — not assumptions",
      ],
    },
    execution: {
      summary: "Created a focused video series featuring:",
      points: [
        "Real patient-driven questions",
        "Doctor-led clarity",
        "Structured, conversational format",
      ],
    },
    outcome: [
      { n: "10,000+", l: "organic views per video" },
      { n: "High", l: "comment engagement" },
      { n: "↑", l: "patient inquiries & bookings" },
    ],
    insight:
      "When communication is built on real audience intent, it doesn't just inform — it drives action.",
    media: {
      type: "videos",
      items: [
        {
          thumb: "/assets/ijn-thumb-1.jpg",
          url: "https://www.youtube.com/watch?v=_WBCtRcW6MM",
          label: "IJN Heart Talk · Episode",
        },
        {
          thumb: "/assets/ijn-thumb-2.jpg",
          url: "https://www.youtube.com/watch?v=sWjbQGXz2CE",
          label: "IJN Heart Talk · Episode",
        },
        {
          thumb: "/assets/ijn-thumb-3.jpg",
          url: "https://www.youtube.com/watch?v=FmrftVNlkls",
          label: "IJN Heart Talk · Episode",
        },
        {
          thumb: "/assets/ijn-thumb-4.jpg",
          url: "https://www.youtube.com/watch?v=mPXmlpiQlzU",
          label: "IJN Heart Talk · Episode",
        },
      ],
    },
  },
  {
    id: "inceif",
    sector: "Finance + Education · Global",
    brand: "INCEIF",
    brandFull: "INCEIF University — The Global University of Islamic Finance",
    headline: "From fragmented messaging to a unified narrative.",
    context:
      "Worked with INCEIF University — one of the world's leading Islamic finance institutions — to strengthen how their programs were positioned.",
    challenge: {
      summary: null,
      points: [
        "Complex subject matter (Islamic finance)",
        "Outdated communication across programs",
        "Lack of a unifying narrative across platforms",
      ],
    },
    approach: {
      summary: "Went back to the core of the brand.",
      points: [
        'Identified a central idea: "Knowledge with Conscience"',
        "Built a campaign that connects Education, Ethics, and Industry relevance",
      ],
    },
    execution: {
      summary: null,
      points: [
        "Repositioned course communication",
        "Repurposed existing content into structured assets",
        "Created a cohesive visual + narrative system across social, campaigns, and program communication",
      ],
    },
    outcome: [
      { n: "↑", l: "differentiation vs competitors" },
      { n: "↑", l: "clarity in program positioning" },
      { n: "↑", l: "student interest & intake" },
    ],
    insight:
      "In knowledge-driven sectors, clarity isn't just branding — it's decision-making leverage.",
    media: {
      type: "images",
      items: [
        { src: "/assets/inceif-covid.png", label: "Thought-leadership campaign" },
        { src: "/assets/inceif-champions.png", label: "Women's Day campaign" },
      ],
    },
  },
];

const SOUND_CHANNEL = "https://www.youtube.com/@R1AILab";

const SOUND = [
  {
    title: "Cahaya-Mu di Dalam Hati",
    subtitle: "Original short film soundtrack · Malay",
    desc:
      "The first glimpse into a story lived in the heart for years — a short film waiting to be born. Music leads, story follows.",
    tag: "Soundtrack · Cinematic",
    duration: "2:48",
    date: "Jun 2026",
    thumb: "/assets/sound-6.jpg",
    url: "https://www.youtube.com/watch?v=73nK-EkISIw",
  },
  {
    title: "Arugile · அருகிலே",
    subtitle: "Tamil cinematic song concept",
    desc:
      "What if heartbreak wasn't the end of love — but the beginning of understanding it? A Tamil song about healing, family, friendship and the love that quietly stands beside us.",
    tag: "Tamil · Cinematic",
    duration: "5:27",
    date: "Jun 2026",
    thumb: "/assets/sound-5.jpg",
    url: "https://www.youtube.com/watch?v=dOnwEsfZHrQ",
  },
  {
    title: "Isn't It Enough",
    subtitle: "A Song About War, Truth & Humanity",
    desc:
      "An anti-war ballad. Not about one war — about every war. About the people the headlines forget, and the question we should keep asking.",
    tag: "Anti-War · Cinematic",
    duration: "3:57",
    date: "Apr 2026",
    thumb: "/assets/sound-4.jpg",
    url: "https://www.youtube.com/watch?v=7BpTi41S3WM",
  },
  {
    title: "Where You Were…",
    subtitle: "A Song of Repentance & Realisation",
    desc:
      "A reckoning with the love that quietly held you up — and the silence after it leaves. Co-written with AI, voiced by emotion.",
    tag: "Ballad · R1: AI Lab",
    duration: "4:48",
    date: "Apr 2026",
    thumb: "/assets/sound-3.jpg",
    url: "https://www.youtube.com/watch?v=4n_w5BU3NME",
  },
  {
    title: "Temple of Your Heart",
    subtitle: "Devotional · Love · Sacred",
    desc:
      "A spiritual passage of love, repentance, and devotion. The moment a soul realises the sacredness of another's heart — and vows to protect it.",
    tag: "Spiritual · R1: AI Lab",
    duration: "5:55",
    date: "Oct 2025",
    thumb: "/assets/sound-2.jpg",
    url: "https://www.youtube.com/watch?v=Qg1RcE-O6WI",
  },
  {
    title: "Together in the Abyss",
    subtitle: "First AI experiment — written, composed and voiced with AI tools",
    desc:
      "The original track. A song about doubt, light, and finding home in the dark — entirely lyric-led, AI-composed.",
    tag: "Ballad · Origin",
    duration: "2:25",
    date: "Oct 2024",
    thumb: "/assets/sound-1.jpg",
    url: "https://www.youtube.com/watch?v=C5zjybj91H0",
  },
];

const ARTICLES = [
  {
    title: "Crime as a Business",
    excerpt:
      "When criminal organisations started thinking like brands. Segmentation, data, speed, trust — the same principles that transformed enterprise now power modern crime.",
    date: "Jul 6, 2026",
    cover:
      "https://media.licdn.com/dms/image/v2/D4D12AQGmggyzAR0msw/article-cover_image-shrink_720_1280/B4DZ84kVwBJ4AU-/0/1783360482438?e=2147483647&v=beta&t=ff43tljTRvbrSZURlwK2P8MgG3DhTD4ZCehgHQFopOE",
    url: "https://www.linkedin.com/pulse/crime-business-rishikesh-n-krkof/",
    tag: "Security · Series 02",
  },
  {
    title: "The Illusion of Security",
    excerpt:
      "Crime in the Age of Digital, Mobile, Crypto and AI. Why every technological breakthrough creates new opportunities for both innovation and exploitation.",
    date: "Jun 23, 2026",
    cover:
      "https://media.licdn.com/dms/image/v2/D4E12AQEQINV7S6ooug/article-cover_image-shrink_720_1280/B4EZ70HV6wIcAQ-/0/1782212030768?e=2147483647&v=beta&t=e6UazQa6P9GsBKANjomfw8RSM6t3q3E2rqNnycODkoA",
    url: "https://www.linkedin.com/pulse/illusion-security-rishikesh-n-wswxe/",
    tag: "Security · Series 01",
  },
  {
    title: "Artificial Intelligence Cheat Code — Cheap & Low Cost",
    excerpt:
      "Same playbook. Different technology. Why every tech wave promises a shortcut — and what to demand before adopting AI.",
    date: "Feb 26, 2026",
    cover:
      "https://media.licdn.com/dms/image/v2/D5612AQHxgckonvwTKw/article-cover_image-shrink_720_1280/B56Zyaq3ATKwAI-/0/1772121454002?e=2147483647&v=beta&t=2gqbOq54IaFGbF1SRTXFLxc9Lebjldj9UG2oyiyX8uo",
    url: "https://www.linkedin.com/pulse/artificial-intelligence-cheat-code-cheap-low-cost-rishikesh-n-ltntc/",
    tag: "AI · Strategy",
  },
  {
    title: "How We Lost the Internet: From Freedom to Control",
    excerpt:
      "We were promised freedom. We got monitored. How convenience and algorithms quietly turned users into products.",
    date: "Feb 16, 2026",
    cover:
      "https://media.licdn.com/dms/image/v2/D5612AQG_suMYOiEb1Q/article-cover_image-shrink_720_1280/B56ZxkZp95G4AI-/0/1771210978454?e=2147483647&v=beta&t=AtBmCzX7aEz0n41gPxCR7bcqyTA6swD8WL583Ry1kLs",
    url: "https://www.linkedin.com/pulse/how-we-lost-internet-from-freedom-control-users-products-rishikesh-n-jd6ac/",
    tag: "Internet · Society",
  },
  {
    title: "AI-Powered Customer Experience: Progress or Personal Disconnection?",
    excerpt:
      "From service to sentience — only when intelligence learns empathy. Why CX needs new metrics in the AI age.",
    date: "Oct 22, 2025",
    cover:
      "https://media.licdn.com/dms/image/v2/D5612AQF8HsTS4bcKUA/article-cover_image-shrink_600_2000/B56ZoLU9mcI0AQ-/0/1761126639923?e=2147483647&v=beta&t=Z5XvJg6P92VIUxI7sgPrEmPPqJLDF2ncX57w_SQPS_Q",
    url: "https://www.linkedin.com/pulse/ai-powered-customer-experience-progress-personal-disconnection-n-v0quc/",
    tag: "AI · CX",
  },
  {
    title: "Tech as the New Culture Maker",
    excerpt:
      "The algorithm is the new myth-maker. How technology — not just marketing — now shapes what people believe and aspire to.",
    date: "Oct 16, 2025",
    cover:
      "https://media.licdn.com/dms/image/v2/D4E12AQFj26NRFlsO7g/article-cover_image-shrink_600_2000/B4EZnrX0nLIQAQ-/0/1760590519947?e=2147483647&v=beta&t=gzamSETiz42FugT1GGEA74ApLPi0mB-z8WmD1wOiOzI",
    url: "https://www.linkedin.com/pulse/tech-new-culture-maker-rishikesh-n-b6u9e/",
    tag: "Tech · Culture",
  },
  {
    title: "The Digital Trust Economy: Why Trust Is the New Brand Currency",
    excerpt:
      "In the AI age, reach gets you seen. Trust keeps you remembered. The case for trust as the rarest currency in the digital economy.",
    date: "Oct 6, 2025",
    cover:
      "https://media.licdn.com/dms/image/v2/D5612AQF_5fGjER3QCA/article-cover_image-shrink_720_1280/B56Zm4J9QwKAAM-/0/1759731251957?e=2147483647&v=beta&t=4u1yhJaOZYg0evwjG6kUaDoDtqz8VEpgk_zqFbAu9oo",
    url: "https://www.linkedin.com/pulse/digital-trust-economy-why-new-brand-currency-rishikesh-n-efykc/",
    tag: "Brand · Trust",
  },
  {
    title: "From Storytelling to Story-Living: Campaigns vs Connected Properties",
    excerpt:
      "Campaigns create moments. Properties create movements. Why brands need ecosystems where consumers don't hear the story — they live it.",
    date: "Sep 23, 2025",
    cover:
      "https://media.licdn.com/dms/image/v2/D5612AQFVt1iSXCAoLw/article-cover_image-shrink_600_2000/B56Zllf5ELJ4AQ-/0/1758344488294?e=2147483647&v=beta&t=dHW7RDjYNEQJljHrYIoLWOv4ZiKSDl5hzPvM2ysYpQI",
    url: "https://www.linkedin.com/pulse/from-storytelling-story-living-campaigns-vs-connected-rishikesh-n-mtjkc/",
    tag: "Brand · Strategy",
  },
  {
    title: "Brands in the Age of AI: Beyond the Hype, Towards Authenticity",
    excerpt:
      "AI doesn't create brand value — it amplifies it. Five steps for brands to use AI wisely without losing their soul.",
    date: "Sep 10, 2025",
    cover:
      "https://media.licdn.com/dms/image/v2/D5612AQFEq5s7PC0pqg/article-cover_image-shrink_600_2000/B56ZkyXwOOG4AQ-/0/1757486714444?e=2147483647&v=beta&t=m45RWzz5IxcaVnw6imZE4ZIltn3umrrKLiDDvD34aQ0",
    url: "https://www.linkedin.com/pulse/brands-age-ai-beyond-hype-towards-authenticity-rishikesh-n-fxxnc/",
    tag: "AI · Brand",
  },
];

/* ============================================================
   COMPONENTS
   ============================================================ */
function Nav() {
  return (
    <nav
      data-testid="main-nav"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-line"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="font-display text-2xl md:text-3xl tracking-tight uppercase"
        >
          Rishikesh<span className="text-gold">.</span>
        </a>
        <div className="hidden md:flex items-center gap-8 text-xs text-[var(--text-muted)] font-body uppercase tracking-[0.2em]">
          <a href="#about" className="link-line hover:text-white" data-testid="nav-about">About</a>
          <a href="#experience" className="link-line hover:text-white" data-testid="nav-experience">Experience</a>
          <a href="#work" className="link-line hover:text-white" data-testid="nav-work">Work</a>
          <a href="#writing" className="link-line hover:text-white" data-testid="nav-writing">Insights</a>
          <a href="#sound" className="link-line hover:text-white" data-testid="nav-sound">Sound</a>
          <a href="#contact" className="link-line hover:text-white" data-testid="nav-contact">Contact</a>
        </div>
        <a
          href={`mailto:${PROFILE.email}?subject=Opportunity%20for%20Rishikesh`}
          data-testid="nav-cta-hire"
          className="hidden md:inline-flex items-center gap-2 text-xs font-body uppercase tracking-[0.25em] border-2 px-6 py-3 hover:bg-gold hover:text-black hover:border-gold transition-colors duration-300"
          style={{ borderColor: "var(--gold)", color: "var(--gold)" }}
        >
          Hire Me <ArrowUpRight size={14} />
        </a>
      </div>
    </nav>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden flex items-center pt-24 pb-16"
    >
      {/* ambient glows */}
      <div className="ambient-glow" style={{ top: "-200px", left: "-100px" }} />
      <div
        className="ambient-glow"
        style={{ bottom: "-200px", right: "-100px", background: "radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center w-full">
        {/* Left: copy */}
        <motion.div
          className="lg:col-span-7 z-10"
          style={{ y, opacity }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex items-center gap-3 mb-8 text-xs font-body uppercase tracking-[0.3em] text-[var(--text-muted)]"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-gold" />
            <Sparkles size={12} className="text-gold" />
            Available for select projects — 2026
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="font-display text-4xl md:text-5xl lg:text-[4.2vw] leading-[1.05] tracking-tight max-w-3xl"
            data-testid="hero-headline"
          >
            I turn communication into{" "}
            <span className="text-gold">business conversations.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-8 max-w-xl text-base md:text-lg text-[var(--text-muted)] font-body leading-relaxed"
            data-testid="hero-tagline"
          >
            Strategic communications consultant with 20+ years across journalism, broadcasting,
            advertising, and AI-led storytelling. I help brands move from{" "}
            <span className="text-white">attention to action.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={`mailto:${PROFILE.email}?subject=Let%27s%20start%20a%20conversation`}
              data-testid="hero-cta-conversation"
              className="group inline-flex items-center gap-3 bg-gold text-black px-7 py-4 text-sm font-body uppercase tracking-[0.2em] hover:bg-white transition-colors duration-300"
            >
              Start a Conversation
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <a
              href={PROFILE.resume}
              download
              data-testid="hero-cta-resume"
              className="group inline-flex items-center gap-3 border border-line text-white px-7 py-4 text-sm font-body uppercase tracking-[0.2em] hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <Download size={16} />
              Download Resume
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-2 max-w-2xl"
            data-testid="hero-stats"
          >
            {[
              { n: "20+", l: "Years Experience" },
              { n: "700+", l: "Projects & Campaigns" },
              { n: null, l: "Multi-Industry" },
              { n: null, l: "Global Exposure" },
            ].map((s, i) => (
              <div
                key={i}
                className={`${i > 0 ? "md:border-l md:border-[var(--line)] md:pl-5" : ""}`}
              >
                {s.n ? (
                  <>
                    <div className="font-display text-3xl md:text-4xl text-gold leading-none mb-2">{s.n}</div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-dim)]">{s.l}</div>
                  </>
                ) : (
                  <>
                    <div className="font-display text-xl md:text-2xl text-white leading-tight mb-1">{s.l}</div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-dim)]">Proven across</div>
                  </>
                )}
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Right: portrait */}
        <motion.div
          className="lg:col-span-5 relative z-0"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.3 }}
          data-testid="hero-portrait-wrap"
        >
          {/* Gold halo behind portrait */}
          <div
            className="absolute -inset-4 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at center, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0) 60%)",
              filter: "blur(20px)",
            }}
            aria-hidden
          />
          <div className="relative aspect-[3/4] w-full overflow-hidden bg-[var(--bg-soft)] border border-line">
            {/* Decorative gold ring */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at 50% 45%, transparent 38%, rgba(212,175,55,0.18) 38.5%, transparent 39.5%)",
              }}
              aria-hidden
            />
            <img
              src={PROFILE.hero}
              alt="Rishikesh — portrait"
              className="hero-portrait relative w-full h-full object-cover"
              loading="eager"
              data-testid="hero-portrait-img"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--text-muted)]">
                Kuala Lumpur · Bengaluru
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
                {PROFILE.title.split("—")[0].trim()}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* scroll indicator */}
      <div className="absolute bottom-6 right-8 text-[10px] uppercase tracking-[0.4em] text-[var(--text-dim)] animate-pulse hidden lg:flex items-center gap-2">
        <span className="inline-block w-3 h-5 border border-[var(--text-dim)] rounded-full relative">
          <span className="absolute top-1 left-1/2 -translate-x-1/2 w-0.5 h-1.5 bg-[var(--text-dim)] rounded-full" />
        </span>
        Scroll
      </div>
    </section>
  );
}

function Trust() {
  // Placeholder logo names — replace with real logo SVGs/images later
  const LOGOS = ["TVS", "Chola", "SBI", "Godrej", "The Hindu", "+ more"];
  return (
    <section
      data-testid="trust-section"
      className="relative py-20 md:py-28 border-t border-line bg-[var(--bg-elev)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center"
        >
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 text-xs font-body uppercase tracking-[0.3em] text-gold mb-5">
              <span className="h-px w-10 bg-gold" />
              Trust
            </div>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl leading-[1.15] tracking-tight text-white">
              Trusted by brands that need communication to{" "}
              <span className="text-gold">perform</span> — not just exist.
            </h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-px bg-[var(--line)] border border-line">
            {LOGOS.map((name, i) => (
              <div
                key={i}
                data-testid={`trust-logo-${i}`}
                className="bg-[var(--bg-elev)] aspect-[3/2] flex items-center justify-center px-4 hover:bg-black transition-colors duration-300 group"
              >
                <span className="font-display text-base md:text-lg text-[var(--text-muted)] group-hover:text-gold transition-colors text-center">
                  {name}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}


function Marquee() {
  return (
    <section
      data-testid="expertise-marquee"
      className="border-y border-line py-8 overflow-hidden bg-[var(--bg-elev)]"
    >
      <div className="marquee-track">
        {[...EXPERTISE, ...EXPERTISE].map((item, i) => (
          <span
            key={i}
            className="font-display text-3xl md:text-5xl px-8 whitespace-nowrap text-[var(--text-muted)] hover:text-gold transition-colors"
          >
            {item} <span className="text-gold mx-4">✦</span>
          </span>
        ))}
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" data-testid="about-section" className="relative py-28 md:py-40">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <div className="flex items-center gap-3 text-xs font-body uppercase tracking-[0.3em] text-gold mb-6">
            <span className="h-px w-10 bg-gold" />
            About
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.0] tracking-tight">
            Communication doesn't fail from
            <span className="text-gold"> lack of creativity.</span>
            <br />
            It fails from lack of <span className="text-gold">clarity.</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="lg:col-span-6 lg:col-start-7 space-y-6 text-base md:text-lg text-[var(--text-muted)] font-body leading-relaxed"
          data-testid="about-body"
        >
          <p>
            I've spent over two decades shaping stories across journalism, broadcast,
            advertising, and now AI-led systems — with two decades of that work
            based between <span className="text-white">Malaysia and India</span>,
            most recently as Director at Vision Reality Tech Studio (VRT Studio),
            Kuala Lumpur & Bengaluru.
          </p>
          <p>
            What I've learned is simple — communication doesn't fail because of lack of
            creativity. It fails because of lack of{" "}
            <span className="text-white">clarity and direction.</span>
          </p>
          <p>That's where I come in.</p>
          <p>
            I don't just help brands say things better. I help them say the{" "}
            <span className="text-white">right</span> things — consistently, strategically,
            and with intent.
          </p>
          <div className="pt-6 grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
            <Detail label="Based in" value={PROFILE.location} />
            <Detail label="Currently" value="Vaikuntha Media" />
            <Detail label="Open to" value="Roles · Projects · Advisory" />
            <Detail label="Travel" value="IDP — Global" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--text-dim)]">{label}</div>
      <div className="text-white mt-1">{value}</div>
    </div>
  );
}

function Experience() {
  return (
    <section
      id="experience"
      data-testid="experience-section"
      className="relative py-28 md:py-40 border-t border-line"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16"
        >
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 text-xs font-body uppercase tracking-[0.3em] text-gold mb-6">
              <span className="h-px w-10 bg-gold" />
              The Arc
            </div>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              Twenty years.
              <br />
              Nine companies.
              <br />
              <span className="text-gold">One craft.</span>
            </h2>
          </div>
          <p className="lg:col-span-4 lg:col-start-9 text-[var(--text-muted)] font-body">
            From a journalist trainee at The Indian Express to building AI-native storytelling
            systems — every chapter sharpened the same instinct: make the message land.
          </p>
        </motion.div>

        <div className="relative rail pl-10" data-testid="experience-timeline">
          {EXPERIENCE.map((e, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.04 }}
              className="relative pb-12 group"
              data-testid={`experience-item-${i}`}
            >
              <span
                className="absolute -left-[34px] top-1.5 w-3.5 h-3.5 rounded-full bg-black border-2 border-gold group-hover:scale-125 transition-transform"
                aria-hidden
              />
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-3 text-[11px] uppercase tracking-[0.2em] text-[var(--text-dim)]">
                  {e.period}
                  <div className="mt-1 text-[var(--text-dim)]">{e.location}</div>
                </div>
                <div className="md:col-span-9">
                  <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">
                    {e.company}
                  </h3>
                  <div className="mt-1 text-sm text-gold uppercase tracking-widest font-body">
                    {e.role}
                  </div>
                  <p className="mt-3 text-[var(--text-muted)] font-body leading-relaxed max-w-3xl">
                    {e.body}
                  </p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

function HighlightCard({ h, idx }) {
  const handleMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <motion.div
      onMouseMove={handleMove}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, delay: idx * 0.08 }}
      className={`spotlight-card p-8 md:p-10 ${h.size === "lg" ? "md:col-span-2" : ""}`}
      data-testid={`highlight-${idx}`}
    >
      <div className="flex items-start justify-between mb-8">
        <span className="font-display text-gold text-2xl">{h.accent}</span>
        <ArrowUpRight size={20} className="text-[var(--text-dim)]" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--text-dim)] mb-3">
        {h.span}
      </div>
      <h3 className="font-display text-2xl md:text-3xl leading-tight text-white mb-4">
        {h.title}
      </h3>
      <p className="text-[var(--text-muted)] font-body leading-relaxed">{h.body}</p>
    </motion.div>
  );
}

function Highlights() {
  return (
    <section
      id="work"
      data-testid="highlights-section"
      className="relative py-28 md:py-40 border-t border-line"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-16 max-w-4xl"
        >
          <div className="flex items-center gap-3 text-xs font-body uppercase tracking-[0.3em] text-gold mb-6">
            <span className="h-px w-10 bg-gold" />
            Strategic Highlights
          </div>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
            Selected work that
            <span className="text-gold"> moved numbers</span>
            <br />
            and minds.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {HIGHLIGHTS.map((h, i) => (
            <HighlightCard key={i} h={h} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ArticleCard({ a, idx }) {
  return (
    <motion.a
      href={a.url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: (idx % 3) * 0.08 }}
      className="group block border border-line bg-[var(--bg-elev)] hover:border-gold transition-colors duration-300"
      data-testid={`article-${idx}`}
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-[var(--bg-soft)]">
        <img
          src={a.cover}
          alt={a.title}
          loading="lazy"
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          style={{ filter: "contrast(1.05) saturate(0.95)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.3em] text-gold bg-black/60 px-3 py-1 backdrop-blur-sm">
          {a.tag}
        </div>
        <ArrowUpRight
          size={20}
          className="absolute top-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </div>
      <div className="p-6 md:p-7">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--text-dim)] mb-3">
          {a.date} · LinkedIn
        </div>
        <h3 className="font-display text-xl md:text-2xl leading-tight text-white group-hover:text-gold transition-colors mb-3">
          {a.title}
        </h3>
        <p className="text-sm text-[var(--text-muted)] font-body leading-relaxed">
          {a.excerpt}
        </p>
        <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold">
          Read on LinkedIn
          <ArrowUpRight
            size={14}
            className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </div>
      </div>
    </motion.a>
  );
}

function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-28 md:py-40 border-t border-line bg-[var(--bg-elev)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16"
        >
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 text-xs font-body uppercase tracking-[0.3em] text-gold mb-6">
              <span className="h-px w-10 bg-gold" />
              What I Do
            </div>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              How I <span className="text-gold">help.</span>
            </h2>
          </div>
          <p className="lg:col-span-4 lg:col-start-9 text-[var(--text-muted)] font-body">
            Five ways I work with founders, brands and teams when communication needs to
            do more than look good — it needs to perform.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--line)] border border-line">
          {SERVICES.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              className="bg-[var(--bg-elev)] hover:bg-black p-8 md:p-10 transition-colors duration-300 group"
              data-testid={`service-${i}`}
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-display text-gold text-xl">{s.n}</span>
                <span className="text-[10px] uppercase tracking-[0.3em] text-[var(--text-dim)] group-hover:text-gold transition-colors">
                  Service
                </span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-white mb-4 leading-tight">
                {s.t}
              </h3>
              <p className="text-[var(--text-muted)] font-body leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CaseStudyBlock({ c, idx }) {
  return (
    <article
      data-testid={`case-${c.id}`}
      className={`relative ${idx > 0 ? "border-t border-line pt-20 md:pt-28" : ""}`}
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
        className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 items-end"
      >
        <div className="lg:col-span-8">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-gold mb-5">
            <span className="font-display text-base text-gold">
              Case 0{idx + 1}
            </span>
            <span className="h-px w-10 bg-gold" />
            {c.sector}
          </div>
          <h3 className="font-display text-3xl md:text-5xl leading-[1.05] tracking-tight text-white mb-4">
            {c.headline}
          </h3>
          <div className="text-sm text-[var(--text-muted)] uppercase tracking-[0.2em] font-body">
            With <span className="text-white">{c.brand}</span> · {c.brandFull}
          </div>
        </div>
        <div className="lg:col-span-3 lg:col-start-10">
          <div className="border border-line p-6 bg-[var(--bg-elev)]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--text-dim)] mb-2">
              Brand
            </div>
            <div className="font-display text-3xl text-gold">{c.brand}</div>
          </div>
        </div>
      </motion.div>

      {/* Body grid: context/challenge/approach/execution + media */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Left: narrative */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7 space-y-10 font-body"
        >
          <CaseBlock label="Context" body={c.context} />
          <CaseBlock label="Challenge" body={c.challenge.summary} points={c.challenge.points} conclusion={c.challenge.conclusion} />
          <CaseBlock label="Approach" body={c.approach.summary} points={c.approach.points} />
          <CaseBlock label="Execution" body={c.execution.summary} points={c.execution.points} />

          {/* Outcome */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-gold" /> Outcome
            </div>
            <div className="grid grid-cols-3 gap-px bg-[var(--line)] border border-line">
              {c.outcome.map((o, j) => (
                <div key={j} className="bg-[var(--bg-elev)] p-5 text-center">
                  <div className="font-display text-3xl md:text-4xl text-gold leading-none mb-2">
                    {o.n}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] leading-snug">
                    {o.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Insight callout */}
          <blockquote className="border-l-2 border-gold pl-6 py-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Key Insight</div>
            <p className="font-display text-xl md:text-2xl text-white leading-snug italic">
              "{c.insight}"
            </p>
          </blockquote>
        </motion.div>

        {/* Right: media */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="lg:col-span-5"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-4 flex items-center gap-3">
            <span className="h-px w-8 bg-gold" />
            {c.media.type === "videos" ? "Watch" : "Visuals"}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {c.media.items.map((m, j) => (
              <a
                key={j}
                href={m.url || m.src}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block aspect-video overflow-hidden border border-line bg-[var(--bg-soft)] hover:border-gold transition-colors"
                data-testid={`case-${c.id}-media-${j}`}
              >
                <img
                  src={m.thumb || m.src}
                  alt={m.label}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                {c.media.type === "videos" && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="w-12 h-12 rounded-full bg-gold/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <span
                        className="block w-0 h-0"
                        style={{
                          borderLeft: "10px solid #0a0a0a",
                          borderTop: "7px solid transparent",
                          borderBottom: "7px solid transparent",
                          marginLeft: "3px",
                        }}
                      />
                    </span>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white">
                    {m.label}
                  </span>
                  <ArrowUpRight size={14} className="text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </a>
            ))}
          </div>
        </motion.div>
      </div>
    </article>
  );
}

function CaseBlock({ label, body, points, conclusion }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-3">
        <span className="h-px w-8 bg-gold" /> {label}
      </div>
      {body && (
        <p className="text-base md:text-lg text-[var(--text-muted)] leading-relaxed mb-3">
          {body}
        </p>
      )}
      {points && points.length > 0 && (
        <ul className="space-y-2 text-base text-[var(--text-muted)]">
          {points.map((p, i) => (
            <li key={i} className="flex gap-3 leading-relaxed">
              <span className="text-gold mt-1.5 shrink-0">·</span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      )}
      {conclusion && (
        <p className="text-base md:text-lg text-white italic mt-4 leading-relaxed">
          {conclusion}
        </p>
      )}
    </div>
  );
}

function CaseStudies() {
  return (
    <section
      id="case-studies"
      data-testid="case-studies-section"
      className="relative py-28 md:py-40 border-t border-line"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-20 max-w-4xl"
        >
          <div className="flex items-center gap-3 text-xs font-body uppercase tracking-[0.3em] text-gold mb-6">
            <span className="h-px w-10 bg-gold" />
            Deep Dive
          </div>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
            Case studies — when communication
            <span className="text-gold"> moved the business.</span>
          </h2>
        </motion.div>

        <div className="space-y-20 md:space-y-28">
          {CASE_STUDIES.map((c, i) => (
            <CaseStudyBlock key={c.id} c={c} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}


function Writing() {
  return (
    <section
      id="writing"
      data-testid="writing-section"
      className="relative py-28 md:py-40 border-t border-line"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16"
        >
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 text-xs font-body uppercase tracking-[0.3em] text-gold mb-6">
              <span className="h-px w-10 bg-gold" />
              Writing
            </div>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              Notes from the
              <br />
              <span className="text-gold">AI age.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 text-[var(--text-muted)] font-body">
            A series on how AI is reshaping branding, communication, culture and trust.
            <a
              href="https://www.linkedin.com/in/shambomahadeva/recent-activity/articles/"
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-3 text-gold link-line text-sm uppercase tracking-[0.25em]"
              data-testid="writing-all-cta"
            >
              See all on LinkedIn ↗
            </a>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {ARTICLES.map((a, i) => (
            <ArticleCard key={i} a={a} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function SoundCard({ s, idx }) {
  return (
    <motion.a
      href={s.url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: (idx % 2) * 0.08 }}
      className="group block border border-line bg-[var(--bg-elev)] hover:border-gold transition-colors duration-300"
      data-testid={`sound-${idx}`}
    >
      <div className="relative aspect-video overflow-hidden bg-[var(--bg-soft)]">
        <img
          src={s.thumb}
          alt={s.title}
          loading="lazy"
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          style={{ filter: "contrast(1.05) saturate(0.95)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
        {/* Play button */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="w-16 h-16 rounded-full bg-gold/95 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
            <span
              className="block w-0 h-0"
              style={{
                borderLeft: "14px solid #0a0a0a",
                borderTop: "9px solid transparent",
                borderBottom: "9px solid transparent",
                marginLeft: "4px",
              }}
            />
          </span>
        </div>
        {/* Top tag */}
        <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.3em] text-gold bg-black/60 px-3 py-1 backdrop-blur-sm">
          {s.tag}
        </div>
        <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.25em] text-white/90 bg-black/60 px-3 py-1 backdrop-blur-sm font-mono">
          {s.duration}
        </div>
      </div>
      <div className="p-6 md:p-7">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--text-dim)] mb-3">
          {s.date} · YouTube
        </div>
        <h3 className="font-display text-xl md:text-2xl leading-tight text-white group-hover:text-gold transition-colors mb-1">
          {s.title}
        </h3>
        <div className="text-[11px] uppercase tracking-[0.2em] text-gold mb-4">
          {s.subtitle}
        </div>
        <p className="text-sm text-[var(--text-muted)] font-body leading-relaxed">
          {s.desc}
        </p>
        <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold">
          Listen on YouTube
          <ArrowUpRight
            size={14}
            className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </div>
      </div>
    </motion.a>
  );
}

function Sound() {
  return (
    <section
      id="sound"
      data-testid="sound-section"
      className="relative py-28 md:py-40 border-t border-line bg-[var(--bg-elev)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16"
        >
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 text-xs font-body uppercase tracking-[0.3em] text-gold mb-6">
              <span className="h-px w-10 bg-gold" />
              Sound · AI Music Lab
            </div>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              Directed by instinct.
              <br />
              <span className="text-gold">Built with AI.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 text-[var(--text-muted)] font-body">
            Conceptualised and directed using AI tools across writing, composition, and voice.
            Exploring how emotion, structure, and storytelling translate when the medium shifts
            from human performance to machine-assisted creation.
            <a
              href={SOUND_CHANNEL}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-3 text-gold link-line text-sm uppercase tracking-[0.25em]"
              data-testid="sound-channel-cta"
            >
              R1: AI Lab on YouTube ↗
            </a>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {SOUND.map((s, i) => (
            <SoundCard key={i} s={s} idx={i} />
          ))}
        </div>

        {/* Footnote */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-12 pt-8 border-t border-line text-xs uppercase tracking-[0.25em] text-[var(--text-dim)] flex flex-wrap items-center justify-between gap-3"
          data-testid="sound-footnote"
        >
          <span>Coming next · AI-generated graphics & storyboards</span>
          <a
            href={SOUND_CHANNEL}
            target="_blank"
            rel="noopener noreferrer"
            className="link-line hover:text-gold"
          >
            Subscribe to R1: AI Lab ↗
          </a>
        </motion.div>
      </div>
    </section>
  );
}




function Languages() {
  return (
    <section
      data-testid="languages-section"
      className="relative py-20 border-t border-line bg-[var(--bg-elev)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3 text-xs font-body uppercase tracking-[0.3em] text-gold mb-4">
            <Globe size={12} />
            Languages
          </div>
          <h3 className="font-display text-3xl md:text-4xl leading-tight">
            Five tongues.
            <br />
            <span className="text-gold">One story.</span>
          </h3>
        </div>
        <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-5 gap-6">
          {LANGUAGES.map((l, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="border-l border-line pl-4"
              data-testid={`lang-${i}`}
            >
              <div className="font-display text-xl text-white">{l.name}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-dim)] mt-1">
                {l.level}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-40 border-t border-line overflow-hidden"
    >
      <div className="ambient-glow" style={{ top: "10%", left: "30%" }} />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-3 text-xs font-body uppercase tracking-[0.3em] text-gold mb-6">
            <span className="h-px w-10 bg-gold" />
            The Brief
          </div>
          <h2 className="font-display text-[14vw] md:text-[10vw] lg:text-[8.5vw] leading-[0.85] tracking-tighter">
            Got a story
            <br />
            worth <span className="text-gold italic">telling?</span>
          </h2>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <motion.a
            href={`mailto:${PROFILE.email}?subject=Opportunity%20for%20Rishikesh`}
            data-testid="contact-email-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 group border border-line p-8 md:p-12 hover:border-gold transition-colors flex flex-col justify-between min-h-[260px]"
          >
            <div className="text-xs uppercase tracking-[0.3em] text-[var(--text-dim)]">
              Direct line
            </div>
            <div>
              <div className="flex items-center gap-4 mb-3">
                <Mail className="text-gold" size={20} />
                <span className="font-display text-2xl md:text-4xl text-white group-hover:text-gold transition-colors">
                  {PROFILE.email}
                </span>
              </div>
              <p className="text-[var(--text-muted)] font-body max-w-lg">
                Best for project briefs, role discussions, advisory engagements and creative
                collaborations. I read everything.
              </p>
            </div>
            <ArrowUpRight
              size={28}
              className="self-end text-[var(--text-dim)] group-hover:text-gold group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
            />
          </motion.a>

          <div className="lg:col-span-5 grid grid-cols-1 gap-6">
            <ContactRow
              testid="contact-phone-my"
              icon={<Phone size={18} />}
              label="Malaysia · Phone"
              value={PROFILE.phones[0].number}
              href={`tel:${PROFILE.phones[0].tel}`}
            />
            <ContactRow
              testid="contact-phone-in"
              icon={<Phone size={18} />}
              label="India · Phone"
              value={PROFILE.phones[1].number}
              href={`tel:${PROFILE.phones[1].tel}`}
            />
            <ContactRow
              testid="contact-linkedin"
              icon={<Linkedin size={18} />}
              label="LinkedIn"
              value="/in/shambomahadeva"
              href={PROFILE.linkedin}
              external
            />
            <ContactRow
              testid="contact-location-my"
              icon={<MapPin size={18} />}
              label="Base · Malaysia"
              value="Kuala Lumpur"
            />
            <ContactRow
              testid="contact-location-in"
              icon={<MapPin size={18} />}
              label="Base · India"
              value="Bengaluru, Karnataka"
            />
            <a
              href={PROFILE.resume}
              download
              data-testid="contact-resume-cta"
              className="group border border-gold text-gold px-6 py-5 flex items-center justify-between hover:bg-gold hover:text-black transition-colors"
            >
              <span className="font-body uppercase tracking-[0.25em] text-sm">Download Résumé</span>
              <Download size={18} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon, label, value, href, external, testid }) {
  const Tag = href ? "a" : "div";
  return (
    <Tag
      data-testid={testid}
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="group border border-line p-5 flex items-center gap-5 hover:border-gold transition-colors"
    >
      <span className="text-gold">{icon}</span>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--text-dim)]">{label}</div>
        <div className="text-white font-body group-hover:text-gold transition-colors">{value}</div>
      </div>
      {href && <ArrowUpRight size={16} className="text-[var(--text-dim)] group-hover:text-gold" />}
    </Tag>
  );
}

function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-line py-10 text-xs font-body uppercase tracking-[0.25em] text-[var(--text-dim)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
        <div>© {new Date().getFullYear()} Rishikesh N · Kuala Lumpur · Bengaluru</div>
        <div className="flex items-center gap-6">
          <a href={PROFILE.linkedin} target="_blank" rel="noopener noreferrer" className="link-line hover:text-gold">
            LinkedIn
          </a>
          <a href={`mailto:${PROFILE.email}`} className="link-line hover:text-gold">
            Email
          </a>
          <a href="#top" className="link-line hover:text-gold">
            Back to top
          </a>
        </div>
      </div>
    </footer>
  );
}

/* ============================================================
   APP
   ============================================================ */
function App() {
  // Route logic
  if (typeof window !== "undefined") {
    const path = window.location.pathname;
    // Teaser is always available at /coming as a backup
    if (path.startsWith("/coming")) {
      return <Teaser />;
    }
    // /site preserved for backwards-compat (links shared during preview phase)
    if (path.startsWith("/site")) {
      return <FullSite />;
    }
    // Pre-launch: root shows the teaser
    if (TEASER_MODE) {
      return <Teaser />;
    }
    // Root path → full site
    if (path === "/" || path === "") {
      return <FullSite />;
    }
    // Anything else → 404
    return <NotFound />;
  }
  return <FullSite />;
}

function FullSite() {
  useEffect(() => {
    document.title =
      "Rishikesh N — Strategic Communications Consultant | Brand · Content · AI";
  }, []);
  return (
    <div className="App">
      <div className="grain" />
      <Nav />
      <main>
        <Hero />
        <Marquee />
        <About />
        <Services />
        <Experience />
        <Highlights />
        <CaseStudies />
        <Writing />
        <Sound />
        <Languages />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
